import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ChevronRight, Wrench, RefreshCw, Settings, Cog, Wind, ArrowLeftRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { BUSINESS_INFO, SERVICES, BRANDS, SERVICE_AREAS, FAQ_ITEMS, IMAGES } from '@/lib/constants';
import { cn } from '@/lib/utils';

const iconMap = {
    Wrench: Wrench,
    RefreshCw: RefreshCw,
    Settings: Settings,
    Cog: Cog,
    Wind: Wind,
    ArrowLeftRight: ArrowLeftRight,
};

const HomePage = () => {
    return (
        <div>
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0">
                    <img 
                        src={IMAGES.hero}
                        alt="Altın Teknik Showroom"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/50" />
                </div>

                <div className="relative z-10 container-custom py-20 md:py-28 lg:py-36">
                    <div className="max-w-2xl">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary/30 text-primary-foreground text-base md:text-lg font-bold mb-6 animate-fade-in border border-primary-foreground/20">
                            <CheckCircle2 className="h-5 w-5" />
                            Airfel Yetkili Bayi & Servis
                        </div>

                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-background leading-tight mb-6 animate-slide-up">
                            <span className="text-[hsl(45_90%_50%)]">Altın</span>{' '}
                            <span className="text-background">Teknik</span>
                            <br />
                            <span className="text-primary text-3xl sm:text-4xl lg:text-5xl">Klima Uzmanı</span>
                        </h1>

                        <p className="text-lg md:text-xl text-background/80 mb-8 leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
                            Antalya'da Airfel yetkili bayi olarak klima satış, montaj, bakım ve onarım hizmetleri. 
                            Profesyonel ekip, güvenilir servis.
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col gap-3 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                            <Button variant="call" size="xl" asChild>
                                <a href={`tel:${BUSINESS_INFO.phone1Clean}`}>
                                    <Phone className="h-5 w-5" />
                                    Hemen Ara: {BUSINESS_INFO.phone1}
                                </a>
                            </Button>
                            <Button variant="call" size="xl" asChild>
                                <a href={`tel:${BUSINESS_INFO.phone2Clean}`}>
                                    <Phone className="h-5 w-5" />
                                    Hemen Ara: {BUSINESS_INFO.phone2}
                                </a>
                            </Button>
                            <Button variant="whatsapp" size="xl" asChild>
                                <a 
                                    href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=Merhaba, klima hizmeti hakkında bilgi almak istiyorum.`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                    WhatsApp: {BUSINESS_INFO.phone2}
                                </a>
                            </Button>
                        </div>

                        {/* Trust Indicators */}
                        <div className="flex flex-wrap gap-6 mt-10 pt-8 border-t border-background/20 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                            <div className="flex items-center gap-2 text-background/80">
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                                <span className="text-sm">Ücretsiz Keşif</span>
                            </div>
                            <div className="flex items-center gap-2 text-background/80">
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                                <span className="text-sm">Aynı Gün Servis</span>
                            </div>
                            <div className="flex items-center gap-2 text-background/80">
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                                <span className="text-sm">Garanti Kapsamı</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="section-padding bg-muted">
                <div className="container-custom">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                            Hizmetlerimiz
                        </h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Klima ihtiyaçlarınız için kapsamlı çözümler sunuyoruz
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SERVICES.map((service, index) => {
                            const IconComponent = iconMap[service.icon];
                            return (
                                <Card 
                                    key={service.id} 
                                    className="card-interactive bg-card border-border/50 hover:border-primary/30"
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className="shrink-0 w-12 h-12 rounded-xl bg-accent flex items-center justify-center">
                                                {IconComponent && <IconComponent className="h-6 w-6 text-accent-foreground" />}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                                    {service.title}
                                                </h3>
                                                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                                                    {service.description}
                                                </p>
                                                <ul className="space-y-2">
                                                    {service.features.map((feature, idx) => (
                                                        <li key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                                                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                                                            {feature}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    <div className="text-center mt-10">
                        <Button variant="default" size="lg" asChild>
                            <Link to="/hizmetler">
                                Tüm Hizmetleri Gör
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Brands Section */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                            Çalıştığımız Markalar
                        </h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Tüm önde gelen klima markalarında uzman servis hizmeti
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                        {BRANDS.map((brand) => (
                            <div 
                                key={brand.name}
                                className={cn(
                                    "px-6 py-3 rounded-xl border text-center font-medium transition-all",
                                    brand.featured
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "bg-card text-foreground border-border hover:border-primary/50 hover:bg-accent"
                                )}
                            >
                                {brand.name}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Service Areas Section */}
            <section className="section-padding bg-muted">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                                Hizmet Bölgelerimiz
                            </h2>
                            <p className="text-muted-foreground text-lg mb-8">
                                Antalya ve çevresinde profesyonel klima hizmeti veriyoruz. 
                                Bulunduğunuz bölgede size en hızlı şekilde ulaşıyoruz.
                            </p>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                                {SERVICE_AREAS.map((area) => (
                                    <div 
                                        key={area}
                                        className="flex items-center gap-2 px-4 py-3 rounded-lg bg-card border border-border"
                                    >
                                        <MapPin className="h-4 w-4 text-primary shrink-0" />
                                        <span className="text-sm font-medium text-foreground">{area}</span>
                                    </div>
                                ))}
                            </div>

                            <Button variant="call" size="lg" asChild>
                                <a href={`tel:${BUSINESS_INFO.phone}`}>
                                    <Phone className="h-5 w-5" />
                                    Servis Talep Et
                                </a>
                            </Button>
                        </div>

                        <div className="relative">
                            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                                <img 
                                    src={IMAGES.showroom}
                                    alt="Altın Teknik Showroom"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-xl shadow-lg hidden md:block">
                                <p className="text-3xl font-bold font-heading">10+</p>
                                <p className="text-sm opacity-90">Yıllık Tecrübe</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                            Neden Bizi Tercih Etmelisiniz?
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { title: 'Yetkili Servis', desc: 'Airfel yetkili bayi ve servis güvencesi', icon: '🏆' },
                            { title: 'Hızlı Müdahale', desc: 'Aynı gün servis imkanı', icon: '⚡' },
                            { title: 'Uygun Fiyat', desc: 'Rekabetçi fiyatlar, şeffaf ücretlendirme', icon: '💰' },
                            { title: 'Garanti', desc: 'İşçilik ve parça garantisi', icon: '✅' },
                        ].map((item, idx) => (
                            <Card key={idx} className="text-center p-6 border-border/50">
                                <div className="text-4xl mb-4">{item.icon}</div>
                                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                    {item.title}
                                </h3>
                                <p className="text-muted-foreground text-sm">{item.desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="section-padding bg-muted">
                <div className="container-custom">
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                                Sıkça Sorulan Sorular
                            </h2>
                            <p className="text-muted-foreground text-lg">
                                Merak ettiğiniz soruların cevapları
                            </p>
                        </div>

                        <Accordion type="single" collapsible className="w-full">
                            {FAQ_ITEMS.map((item, index) => (
                                <AccordionItem 
                                    key={index} 
                                    value={`item-${index}`}
                                    className="bg-card rounded-lg mb-3 border border-border px-4"
                                >
                                    <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline py-5">
                                        {item.question}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-muted-foreground pb-5">
                                        {item.answer}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-primary">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-4">
                        Klima Hizmeti İçin Hemen Arayın
                    </h2>
                    <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
                        Profesyonel ekibimiz sorularınızı yanıtlamak ve size en uygun çözümü sunmak için hazır.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            size="xl" 
                            className="bg-background text-primary hover:bg-background/90 font-semibold shadow-lg"
                            asChild
                        >
                            <a href={`tel:${BUSINESS_INFO.phone}`}>
                                <Phone className="h-5 w-5" />
                                {BUSINESS_INFO.phoneDisplay}
                            </a>
                        </Button>
                        <Button variant="whatsapp" size="xl" asChild>
                            <a 
                                href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=Merhaba, klima hizmeti hakkında bilgi almak istiyorum.`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                WhatsApp İle Yazın
                            </a>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
